import React, { useRef, useEffect } from 'react';

type VideoSegment = { start: number; end: number };

interface TimelineBarProps {
    value: number;
    segments: VideoSegment[][];
}

const VideoChart: React.FC<TimelineBarProps> = ({ value, segments }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const minTime = 0;
    const maxTime = 86399;
    const timeSpan = maxTime - minTime;

    const draw = () => {
        const canvas = canvasRef.current!;
        const container = containerRef.current!;
        const ctx = canvas.getContext('2d')!;
        const dpr = window.devicePixelRatio || 1;
        const canvasHeight = 48;
        const drawWidth = container.clientWidth || 800;

        canvas.width = drawWidth * dpr;
        canvas.height = canvasHeight * dpr;
        canvas.style.width = `${drawWidth}px`;
        canvas.style.height = `${canvasHeight}px`;
        ctx.scale(dpr, dpr);
        ctx.clearRect(0, 0, drawWidth, canvasHeight);
        ctx.fillStyle = '#444';
        ctx.fillRect(0, 20, drawWidth, 14);
        ctx.fillStyle = '#ff9500';
        segments.forEach((row) => {
            row.forEach((segment) => {
                const startX = ((segment.start - minTime) / timeSpan) * drawWidth;
                const endX = ((segment.end - minTime) / timeSpan) * drawWidth;
                ctx.fillRect(startX, 20, endX - startX, 14);
            });
        });

        ctx.font = '8px Arial';
        ctx.textBaseline = 'top';
        ctx.setLineDash([2, 2]);

        let lastLabelEnd = -Infinity;
        const PADDING = 6;

        for (let t = 0; t <= maxTime; t += 3600) {
            const x = ((t - minTime) / timeSpan) * drawWidth;
            const hour24 = Math.floor(t / 3600);

            let label = '';
            if (hour24 === 0) label = '12am';
            else if (hour24 === 12) label = '12pm';
            else if (hour24 < 12) label = `${hour24}am`;
            else label = `${hour24 - 12}pm`;

            ctx.font = '10px Arial';
            const textWidth = ctx.measureText(label).width;

            let textX = x;
            let labelStart = 0;
            let labelEnd = 0;

            // ✅ Correct alignment-based bounds
            if (t === 0) {
                ctx.textAlign = 'left';
                textX = x + 2;

                labelStart = textX;
                labelEnd = textX + textWidth;
            }
            else if (t >= maxTime - 3600) {
                ctx.textAlign = 'right';
                textX = x - 2;

                labelStart = textX - textWidth;
                labelEnd = textX;
            }
            else {
                ctx.textAlign = 'center';

                labelStart = textX - textWidth / 2;
                labelEnd = textX + textWidth / 2;
            }

            // 🚀 HARD CLAMP inside canvas (prevents edge collision)
            if (labelStart < 0) {
                labelStart = 2;
                labelEnd = labelStart + textWidth;
                textX = labelStart;
                ctx.textAlign = 'left';
            }

            if (labelEnd > drawWidth) {
                labelEnd = drawWidth - 2;
                labelStart = labelEnd - textWidth;
                textX = labelEnd;
                ctx.textAlign = 'right';
            }

            // 🚀 FINAL overlap check
            if (labelStart < lastLabelEnd + PADDING) continue;

            // Draw tick
            ctx.strokeStyle = '#888';
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(x, 20);
            ctx.lineTo(x, 34);
            ctx.stroke();

            // Draw label
            ctx.fillStyle = '#aaa';
            ctx.fillText(label, textX, 4);

            lastLabelEnd = labelEnd;
        }

        ctx.setLineDash([]);
        const currentX = ((value - minTime) / timeSpan) * drawWidth;
        ctx.strokeStyle = '#ff0000';
        ctx.lineWidth = 2;
        ctx.shadowColor = 'rgba(255,0,0,0.6)';
        ctx.shadowBlur = 4;
        ctx.beginPath();
        ctx.moveTo(currentX, 14);
        ctx.lineTo(currentX, 38);
        ctx.stroke();
        ctx.fillStyle = '#ff0000';
        ctx.beginPath();
        ctx.moveTo(currentX - 5, 14);
        ctx.lineTo(currentX + 5, 14);
        ctx.lineTo(currentX, 20);
        ctx.closePath();
        ctx.fill();
        ctx.shadowBlur = 0;
    };

    useEffect(() => {
        draw();
    }, [segments, value]);
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;
        const ro = new ResizeObserver(() => draw());
        ro.observe(container);
        return () => ro.disconnect();
    }, [segments, value]);

    return (
        <div
            style={{
                width: '100%',
                background: 'rgba(0,0,0,0.75)',
                borderRadius: '6px',
                padding: '6px 10px',
                boxSizing: 'border-box',
                pointerEvents: 'none',
            }}
        >
            <div
                ref={containerRef}
                style={{
                    width: '100%',
                    height: '48px',
                    cursor: 'default',
                }}
            >
                <canvas ref={canvasRef} style={{ display: 'block' }} />
            </div>
        </div>
    );
};

export default React.memo(VideoChart);